// ══════════════════════════════════════════════════════════
//  #FLEET_TELEM_ALERT_V1 (2026-10-12) — Camera / GPS not reporting, on the Fleet dash.
//
//  Bill: "Need a Trigger for any cameras or GPS Units not reporting. Should land into Fleet DASH for now and once
//  we build out how this looks we can pipe into other places."
//  #FLEET_TELEM_HOT_V1 (2026-10-12, same day) — Bill: "This is a HOT one and should land up here in these tiles. Need
//  a search, Sort and Type and ability to print." → a red tile in the Fleet summary row (count of Master units with a
//  silent camera / GPS) that opens its own screen: search, Issue (GPS / Camera) + Type (Master category) filters,
//  click-to-sort columns, Print (letter landscape, prints exactly what is filtered on screen).
//
//  Reads what is already pulled — nothing new to sync:
//    fleet-geotab-maint   positions[] (last fix + Geotab's own communicating flag) and faults[] (the tracker's own
//                         messages, e.g. "Camera dismounted", "GPS module not responding") — api/fleet-geotab-maint
//    fleet-equipment      the Master's Camera / GPS columns (NW = has one, not working)
//
//  A unit lands on the list when:
//    GPS     the tracker has not reported in 3+ days or Geotab marks it not communicating (same rule as the
//            Maintenance → Dark Trackers tab and the red dots on the GPS map), or the tracker itself reported a
//            hardware fault (GPS module not responding / remove device), or the Master says GPS = NW
//    Camera  the tracker reported a camera message in the last 14 days (dismounted, disconnected, video fault),
//            or the Master says Camera = NW
//  Trackers on a retired (sold) unit are left off. A tracker with no Master match shows by its Geotab name.
//
//  "Got it" (Fleet edit only) parks one issue with an optional reason — e.g. a trailer parked out of coverage.
//  It is keyed to what was seen (the last-heard time / the fault time), so if the unit reports again and later
//  goes silent again, or the camera throws a new message, it comes back on its own. Stored in 'fleet-telem-ack'.
//  "Show parked" lists them with who / why, and "Unpark" puts one back.
//
//  #FLEET_TELEM_STOCK_V1 (2026-10-12) — Bill: the unmatched trackers that have NEVER reported are "likely stock items not
//  yet installed." A tracker with no Master match and no position ever is left off entirely (counted in the footnote);
//  one that reported and then went quiet still shows under "+ N not on Master" — it is on something real.
//  #FLEET_TELEM_ASSIGN_V1 (2026-10-12) — Bill: "Last assigned to would be helpful." Read from the Daily Inventory
//  (fleet-daily-<Monday>), newest day first, up to ASSIGN_WEEKS back. Shows the newest name on the sheet; when that
//  name is SHOP / RANCH / SOLD-type and a crew had it before, the last crew name is shown under it. Loaded once when
//  the screen opens (the tile does not need it). Search, sort and Print all include it.
//
//  telemIssues() is exported on its own so the same list can be piped to the Owner dash, an email or SMS later.
// ══════════════════════════════════════════════════════════
import { useEffect, useMemo, useState, useCallback } from "react";
import { vinClean, unitKey } from "./unitValid";

export const TELEM_ACK_KEY = 'fleet-telem-ack';
const GEO_KEY = 'fleet-geotab-maint';
const DARK_DAYS = 3;
const CAM_DAYS = 14;
const GPS_BOX = /GPS module not responding|remove device/i;
const CAM_MSG = /camera|video|dashcam/i;
const RED = '#F87171';
const ASSIGN_WEEKS = 12;
const NOT_CREW = /^(SHOP|RANCH|SOLD|STOLEN|YARD|IDLE|WELDER)$/i;
const pad2 = (n) => String(n).padStart(2, '0');
const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const mdy = (iso) => { const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : ''; };
// unitKey → { name, duc, day, crew, crewDay } from the Daily Inventory, newest first.
const loadAssign = async (sg) => {
  const mon = new Date(); mon.setHours(12, 0, 0, 0); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
  const keys = []; for (let i = 0; i < ASSIGN_WEEKS; i++) { const d = new Date(mon); d.setDate(d.getDate() - 7 * i); keys.push('fleet-daily-' + isoOf(d)); }
  const weeks = await Promise.all(keys.map(async k => { try { return await sg(k); } catch { return null; } }));
  const m = {};
  weeks.forEach(wk => {
    if (!wk || typeof wk !== 'object') return;
    Object.keys(wk).filter(k => /^\d{4}-\d{2}-\d{2}$/.test(k)).sort().reverse().forEach(day => {
      const rec = wk[day] || {};
      Object.keys(rec).forEach(uk => {
        const c = rec[uk] || {};
        const name = String(c.assigned || '').trim().toUpperCase();
        if (!name) return;
        const x = m[uk] || (m[uk] = {});
        if (!x.name) { x.name = name; x.duc = String(c.duc || '').trim().toUpperCase(); x.day = day; }
        if (!x.crew && !NOT_CREW.test(name)) { x.crew = name; x.crewDay = day; }
      });
    });
  });
  return m;
};
export const assignText = (a) => !a ? '' : (a.crew && a.crew !== a.name ? `${a.name} (last crew ${a.crew} ${mdy(a.crewDay)})` : `${a.name} ${mdy(a.day)}`);
const CAM_C = '#C4B5FD';

const daysSince = (v, now) => { const t = v ? new Date(v).getTime() : NaN; return isFinite(t) ? (now - t) / 86400000 : null; };
const fmtWhen = (v) => {
  const d = v ? new Date(v) : null; if (!d || isNaN(d)) return '';
  let h = d.getHours(); const ap = h >= 12 ? 'p' : 'a'; h = h % 12 || 12;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)} ${h}:${String(d.getMinutes()).padStart(2, '0')}${ap}`;
};
const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// → { list:[row], onMasterN, gpsN, camN, parkedN, ... } where row = { id, unit, desc, cat, onMaster, geoName,
//   issues:[{ kind:'gps'|'cam', key, label, when, days, ackKey, acked }], live:[issues not parked], gps, cam, worst, last }
// Every issue is returned; parked ones carry `acked` (the 'fleet-telem-ack' entry) and do not count.
export const telemIssues = ({ geo, units, retired, ack, now }) => {
  const t0 = now || Date.now();
  const byVin = {}; (units || []).forEach(u => { const v = vinClean(u.vin); if (v.length >= 5) byVin[v] = u; });
  // #TELEM_NA_WINS_V1 (2026-10-26) — Bill: 19-0034 / 22-2664 are being sold, trackers stripped out, but Geotab still lists the
  // boxes as silent so they sat on the list forever. Master GPS = N/A (or Camera = N/A) now means "not required / removed" and
  // wins over anything Geotab says for that unit, so a mechanic can clear these by setting N/A.
  const naOf = (vin, kind) => { const u = byVin[vinClean(vin)]; return !!u && String((kind === 'gps' ? u.gpsStatus : u.cameraStatus) || '').trim().toUpperCase() === 'N/A'; };
  const goneVin = new Set(); (retired || []).forEach(u => { const v = vinClean(u.vin); if (v.length >= 5 && !byVin[v]) goneVin.add(v); });
  const rows = {};
  let retiredSkipped = 0, stockSkipped = 0;
  const rowFor = (vin, name) => {
    const v = vinClean(vin);
    if (v && goneVin.has(v)) return null;
    const u = v ? byVin[v] : null;
    const id = u ? `u:${u.unit}` : (v ? `v:${v}` : `n:${String(name || '').trim()}`);
    if (!rows[id]) rows[id] = { id, unit: u ? u.unit : String(name || '').trim() || '(no name)', desc: u ? (u.desc || '') : '', cat: u ? (u.cat || '') : '', onMaster: !!u, geoName: String(name || '').trim(), issues: [] };
    return rows[id];
  };
  const add = (row, iss) => {
    if (!row) return;
    if (row.issues.some(x => x.key === iss.key)) return;
    const ackKey = `${row.id}|${iss.key}|${iss.stamp || ''}`;
    row.issues.push({ ...iss, ackKey, acked: (ack && ack[ackKey]) || null });
  };
  const g = geo || {};
  (g.positions || []).forEach(p => {
    const v = vinClean(p.vin);
    const age = daysSince(p.at, t0);
    const dark = p.communicating === false || age == null || age > DARK_DAYS;
    if (v && goneVin.has(v)) { if (dark) retiredSkipped++; return; }
    if (dark && age == null && !(v && byVin[v])) { stockSkipped++; return; }   // #FLEET_TELEM_STOCK_V1 — never installed
    if (dark && naOf(p.vin, 'gps')) return;   // #TELEM_NA_WINS_V1
    if (dark) add(rowFor(p.vin, p.name), { kind: 'gps', key: 'gps-dark', stamp: p.at || 'never',
      label: age == null ? 'GPS — never reported' : `GPS silent ${Math.max(0, Math.floor(age))}d`, when: p.at, days: age == null ? 9999 : age });
  });
  (g.faults || []).forEach(f => {
    if (!f || f.dismissed) return;
    const d = String(f.desc || '');
    const age = daysSince(f.lastAt, t0);
    if (f.tracker && GPS_BOX.test(d)) {
      if (naOf(f.vin, 'gps')) return;   // #TELEM_NA_WINS_V1
      add(rowFor(f.vin, f.name), { kind: 'gps', key: 'gps-box', stamp: f.lastAt, label: 'GPS box fault', detail: d, when: f.lastAt, days: age || 0 });
    } else if (CAM_MSG.test(d) && (age == null || age <= CAM_DAYS)) {
      if (naOf(f.vin, 'cam')) return;   // #TELEM_NA_WINS_V1
      add(rowFor(f.vin, f.name), { kind: 'cam', key: 'cam-msg', stamp: f.lastAt, label: `Camera — ${d.replace(/^camera\s*/i, '') || 'fault'}`, detail: d, when: f.lastAt, days: age || 0 });
    }
  });
  (units || []).forEach(u => {
    const gs = String(u.gpsStatus || '').trim().toUpperCase(), cs = String(u.cameraStatus || '').trim().toUpperCase();
    if (gs !== 'NW' && cs !== 'NW') return;
    const id = `u:${u.unit}`;   // keyed by unit # even when the Master row has no VIN typed in
    if (!rows[id]) rows[id] = { id, unit: u.unit, desc: u.desc || '', cat: u.cat || '', onMaster: true, geoName: '', issues: [] };
    if (gs === 'NW') add(rows[id], { kind: 'gps', key: 'gps-nw', stamp: 'NW', label: 'Master: GPS not working', days: 0 });
    if (cs === 'NW') add(rows[id], { kind: 'cam', key: 'cam-nw', stamp: 'NW', label: 'Master: Camera not working', days: 0 });
  });
  const list = Object.values(rows).filter(r => r.issues.length);
  list.forEach(r => {
    r.live = r.issues.filter(x => !x.acked);
    r.gps = r.live.some(x => x.kind === 'gps'); r.cam = r.live.some(x => x.kind === 'cam');
    r.worst = Math.max(0, ...r.live.map(x => x.days || 0));
    r.last = r.issues.map(x => x.when || '').sort().pop() || '';
  });
  const liveM = list.filter(r => r.onMaster && r.live.length);
  return {
    list, onMasterN: liveM.length, gpsN: liveM.filter(r => r.gps).length, camN: liveM.filter(r => r.cam).length,
    offMasterN: list.filter(r => !r.onMaster && r.live.length).length,
    parkedN: list.reduce((a, r) => a + r.issues.filter(x => x.acked).length, 0),
    retiredSkipped, stockSkipped, generatedAt: g.generatedAt || null,
  };
};

// #TELEM_FLAG_V1 (2026-10-21) — Bill: "Yard Walk: Can we include GPS Units that are not reporting. also on Down Units...
// basically everywhere a Unit pops up a warning when GPS unit is not working and/or needs service."
// telemFlagIndex() → { [unitKey]: { gps, cam, title } } where gps / cam = 'down' (on the Cam / GPS down list — Geotab silent,
// tracker fault, or Master NW; parked issues left off) or 'need' (Master says NO GPS / NO CAM — missing, needs one).
// <TelemFlag f={...} /> is the small chip set shown beside a unit # — red = down, amber = needs one. Nothing shows when fine.
export const telemFlagIndex = (res, units) => {
  const m = {};
  const at = (unit) => { const k = unitKey(unit); if (!k) return null; return m[k] || (m[k] = { gps: null, cam: null, why: [] }); };
  ((res && res.list) || []).forEach(r => {
    if (!r.onMaster || !r.live || !r.live.length) return;
    const x = at(r.unit); if (!x) return;
    if (r.gps) x.gps = 'down'; if (r.cam) x.cam = 'down';
    r.live.forEach(i => x.why.push(i.label));
  });
  (units || []).forEach(u => {
    const gs = String(u.gpsStatus || '').trim().toUpperCase(), cs = String(u.cameraStatus || '').trim().toUpperCase();
    if (gs !== 'NO GPS' && cs !== 'NO CAM') return;
    const x = at(u.unit); if (!x) return;
    if (gs === 'NO GPS' && !x.gps) { x.gps = 'need'; x.why.push('Master: no GPS — needs one'); }
    if (cs === 'NO CAM' && !x.cam) { x.cam = 'need'; x.why.push('Master: no camera — needs one'); }
  });
  Object.values(m).forEach(x => { x.title = x.why.join('\n'); });
  return m;
};
export const telemFlagFor = (ix, unit) => (ix && unit ? ix[unitKey(unit)] || null : null);
const FLAG_C = { down: '#F87171', need: '#FBBF24' };
export function TelemFlag({ f, style }) {
  if (!f || (!f.gps && !f.cam)) return null;
  const one = (kind, st) => !st ? null : (
    <span key={kind} style={{ fontSize: 9, fontWeight: 900, letterSpacing: .4, color: FLAG_C[st], border: `1px solid ${FLAG_C[st]}`, borderRadius: 3,
      padding: '1px 5px', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
      {st === 'down' ? `⚠ ${kind} DOWN` : `NO ${kind}`}
    </span>);
  return <span title={f.title} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', verticalAlign: 'middle', ...(style || {}) }}>{one('GPS', f.gps)}{one('CAM', f.cam)}</span>;
}

// Loads the Geotab blob + parked list; re-reads every 10 min while `on`. Used by the dash tile and the screen.
export const useTelemIssues = (sg, units, retired, on, assignOn) => {
  const [geo, setGeo] = useState(null);
  const [ack, setAck] = useState({});
  const [assign, setAssign] = useState(null);   // #FLEET_TELEM_ASSIGN_V1 — null = not loaded
  useEffect(() => {
    if (!assignOn || assign) return undefined;
    let dead = false;
    loadAssign(sg).then(m => { if (!dead) setAssign(m); }).catch(() => { if (!dead) setAssign({}); });
    return () => { dead = true; };
  }, [assignOn, assign, sg]);
  const reload = useCallback(async () => {
    try { const g = await sg(GEO_KEY); setGeo(g && typeof g === 'object' ? g : {}); } catch { setGeo(p => p || {}); }
    try { const a = await sg(TELEM_ACK_KEY); setAck(a && typeof a === 'object' ? a : {}); } catch { /* none yet */ }
  }, [sg]);
  useEffect(() => {
    if (!on) return undefined;
    reload();
    const t = setInterval(reload, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [on, reload]);
  const res = useMemo(() => telemIssues({ geo, units, retired, ack }), [geo, units, retired, ack]);
  return { res, ack, setAck, reload, loaded: !!geo, assign };
};

const SORTS = {
  unit: r => String(r.unit || ''),
  desc: r => String(r.desc || r.geoName || '').toUpperCase(),
  cat: r => String(r.cat || '~').toUpperCase(),
  issue: r => (r.gps ? 0 : 1) + (r.cam ? 0 : 2),
  days: r => r.worst || 0,
  last: r => r.last || '',
};
const ASG_SORT = (a) => (a ? (a.crew && a.crew !== a.name ? a.name + ' ' + a.crew : a.name) : '~');

export default function FleetTelemList({ data, sg, ss, upn, theme, canAck, shopUnits, openViewer, onBack }) {
  const S = theme || {};
  const { res, setAck } = data;
  const assign = data.assign;   // #FLEET_TELEM_ASSIGN_V1
  const asg = useCallback((r) => (r.onMaster && assign) ? (assign[unitKey(r.unit)] || null) : null, [assign]);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');      // all | gps | cam
  const [cat, setCat] = useState('');           // '' = every type · '__off' = not on the Master
  const [sort, setSort] = useState({ k: 'days', dir: 'desc' });
  const [showOff, setShowOff] = useState(false);
  const [showParked, setShowParked] = useState(false);
  const [ackFor, setAckFor] = useState(null);   // { row, iss }
  const [ackWhy, setAckWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const shopSet = useMemo(() => new Set((shopUnits || []).map(s => String(s).toUpperCase())), [shopUnits]);

  const cats = useMemo(() => {
    const m = {};
    res.list.filter(r => r.onMaster && r.live.length).forEach(r => { const c = String(r.cat || '').trim() || '(no type)'; m[c] = (m[c] || 0) + 1; });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
  }, [res]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const out = res.list.filter(r => {
      const issues = showParked ? r.issues : r.live;
      if (!issues.length) return false;
      if (cat === '__off') { if (r.onMaster) return false; }
      else if (!r.onMaster && !showOff) return false;
      else if (cat && (String(r.cat || '').trim() || '(no type)') !== cat) return false;
      if (kind !== 'all' && !issues.some(x => x.kind === kind)) return false;
      if (s && ![r.unit, r.desc, r.cat, r.geoName, assignText(asg(r)), ...issues.map(x => `${x.label} ${x.detail || ''} ${(x.acked && x.acked.why) || ''}`)]
        .some(v => String(v || '').toLowerCase().includes(s))) return false;
      return true;
    });
    const f = sort.k === 'asg' ? (r => ASG_SORT(asg(r))) : (SORTS[sort.k] || SORTS.days), d = sort.dir === 'asc' ? 1 : -1;
    return out.sort((a, b) => { const x = f(a), y = f(b); return (x < y ? -1 : x > y ? 1 : 0) * d || String(a.unit).localeCompare(String(b.unit)); });
  }, [res, q, kind, cat, sort, showOff, showParked, asg]);

  const setSortK = (k) => setSort(p => p.k === k ? { k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { k, dir: (k === 'days' || k === 'last') ? 'desc' : 'asc' });

  const writeAck = async (mutate) => {
    setBusy(true);
    try {
      let cur = {};
      try { const a = await sg(TELEM_ACK_KEY); if (a && typeof a === 'object') cur = a; } catch { /* first one */ }
      const next = mutate({ ...cur });
      const cut = Date.now() - 120 * 86400000;   // keep the store small
      Object.keys(next).forEach(k => { const t = new Date(next[k] && next[k].at).getTime(); if (isFinite(t) && t < cut) delete next[k]; });
      await ss(TELEM_ACK_KEY, next);
      setAck(next);
      return true;
    } catch (e) { alert('Could not save: ' + (e.message || e)); return false; }
    finally { setBusy(false); }
  };
  const saveAck = async () => {
    if (!ackFor) return;
    const ok = await writeAck(n => ({ ...n, [ackFor.iss.ackKey]: { by: upn || '', at: new Date().toISOString(), why: ackWhy.trim(), unit: ackFor.row.unit, issue: ackFor.iss.label } }));
    if (ok) { setAckFor(null); setAckWhy(''); }
  };
  const unpark = (iss) => writeAck(n => { delete n[iss.ackKey]; return n; });

  const kindLabel = kind === 'gps' ? 'GPS' : kind === 'cam' ? 'Camera' : 'GPS + Camera';
  const printList = () => {
    const title = 'Camera / GPS Not Reporting';
    const v = openViewer ? openViewer(title) : null;
    if (!v) { alert('Pop-up blocked — allow pop-ups for this site to print.'); return; }
    const filt = [kindLabel, cat === '__off' ? 'Not on Master' : (cat || 'All types'), q.trim() ? `search "${q.trim()}"` : '', showParked ? 'incl. parked' : ''].filter(Boolean).join(' · ');
    const body = rows.map((r, i) => {
      const issues = showParked ? r.issues : r.live;
      return `<tr><td class="n">${i + 1}</td><td class="u">${esc(r.unit)}${shopSet.has(String(r.unit).toUpperCase()) ? ' <span class="tag">IN SHOP</span>' : ''}${r.onMaster ? '' : '<div class="sm">not on Master</div>'}</td>`
        + `<td>${esc(r.desc || r.geoName)}</td><td>${esc(r.cat)}</td><td>${(() => { const a = asg(r); return !a ? '' : `<b>${esc(a.name)}</b>${a.duc && !/^A-/.test(a.duc) ? ' ' + esc(a.duc) : ''} <span class="sm">${esc(mdy(a.day))}</span>${a.crew && a.crew !== a.name ? `<div class="sm">last crew ${esc(a.crew)} ${esc(mdy(a.crewDay))}</div>` : ''}`; })()}</td>`
        + `<td>${issues.map(x => `<div><b class="${x.kind}">${x.kind === 'cam' ? 'CAM' : 'GPS'}</b> ${esc(x.label)}${x.acked ? ` <i>(parked${x.acked.why ? ': ' + esc(x.acked.why) : ''})</i>` : ''}</div>`).join('')}</td>`
        + `<td class="c">${issues.filter(x => x.when).map(x => `<div>${esc(fmtWhen(x.when))}</div>`).join('')}</td><td class="chk"></td></tr>`;
    }).join('') || '<tr><td colspan="8" class="c">Nothing in this view.</td></tr>';
    const logo = ((typeof window !== 'undefined' && window.location) ? window.location.origin : '') + '/ddd-logo-black.png';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title} ${new Date().toISOString().slice(0, 10)}</title><style>
      @page { size: letter landscape; margin: 8mm; }
      body { font: 11px Arial, sans-serif; color:#111; margin:0; padding:10px; }
      .vbar { position:sticky; top:0; display:flex; align-items:center; gap:8px; background:#111; color:#fff; font:700 14px Arial; padding:8px 10px; margin:-10px -10px 10px; }
      @media print { .vbar { display:none; } }
      .top { display:flex; align-items:center; gap:14px; border-bottom:2px solid #111; padding-bottom:6px; margin-bottom:6px; }
      .top img { height:38px; } .top h1 { font-size:18px; margin:0; } .top .f { font-size:10.5px; color:#444; }
      .sum { font-size:11.5px; margin:4px 0 8px; } .sum b { color:#B91C1C; }
      table { width:100%; border-collapse:collapse; } th { background:#E5E7EB; text-align:left; font-size:10px; text-transform:uppercase; padding:4px 5px; border:1px solid #9CA3AF; }
      td { padding:4px 5px; border:1px solid #D1D5DB; vertical-align:top; } tr { page-break-inside:avoid; }
      .n { width:22px; text-align:center; color:#555; } .u { font-weight:700; white-space:nowrap; } .c { text-align:center; white-space:nowrap; } .chk { width:70px; }
      b.gps { color:#B91C1C; font-size:9px; } b.cam { color:#6D28D9; font-size:9px; } .sm { font-size:9px; font-weight:400; color:#666; }
      .tag { font-size:8.5px; border:1px solid #111; padding:0 3px; } i { color:#555; }
      .foot { margin-top:8px; font-size:9.5px; color:#555; }
    </style></head><body>
      <div class="top"><img src="${logo}" alt=""><div><h1>${title}</h1><div class="f">${esc(filt)} · printed ${esc(fmtWhen(new Date().toISOString()))}${res.generatedAt ? ` · Geotab pulled ${esc(fmtWhen(res.generatedAt))}` : ''}</div></div></div>
      <div class="sum"><b>${rows.length}</b> unit${rows.length === 1 ? '' : 's'} on this sheet · Master totals: <b>${res.gpsN}</b> GPS · <b>${res.camN}</b> camera</div>
      <table><thead><tr><th>#</th><th>Unit</th><th>Description</th><th>Type</th><th>Last assigned</th><th>What is wrong</th><th>Last heard / seen</th><th>Fixed / note</th></tr></thead><tbody>${body}</tbody></table>
      <div class="foot">GPS = no position in ${DARK_DAYS}+ days, Geotab says not communicating, a tracker hardware fault, or Master GPS = NW. Camera = a camera message from the tracker in the last ${CAM_DAYS} days, or Master Camera = NW.</div>
    </body></html>`;
    v.show(html);
  };

  const line = S.panelBorder || '#3A3D42', steel = S.steel || '#9AA0A8', text = S.text || '#EDEEF0', accent = S.accent || '#FF7A1A';
  const chip = (on, label, n, k) => (
    <button key={k} onClick={() => setKind(k)} style={{ cursor: 'pointer', background: on ? accent : 'transparent', color: on ? '#1A1300' : steel,
      border: `1px solid ${on ? accent : line}`, borderRadius: 20, padding: '4px 11px', fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: .3 }}>{label} {n}</button>
  );
  const inp = { background: '#15171A', color: text, border: `1px solid ${line}`, borderRadius: 7, padding: '6px 9px', fontSize: 12.5 };
  const th = (k, l) => (
    <th key={l} onClick={k ? () => setSortK(k) : undefined} style={{ textAlign: 'left', fontSize: 9.5, fontWeight: 900, letterSpacing: .4, textTransform: 'uppercase',
      color: sort.k === k ? accent : steel, padding: '6px 8px', borderBottom: `1px solid ${line}`, whiteSpace: 'nowrap', cursor: k ? 'pointer' : 'default', userSelect: 'none' }}>
      {l}{sort.k === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
  const td = { fontSize: 12, color: text, padding: '6px 8px', borderBottom: `1px solid ${line}`, verticalAlign: 'top' };

  return (
    <div style={{ background: S.panel || '#1C2024', border: `1px solid ${line}`, borderLeft: `3px solid ${res.onMasterN ? RED : (S.good || '#4ADE80')}`, borderRadius: 12, padding: 14,
      boxShadow: res.onMasterN ? `0 0 22px -8px ${RED}AA` : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        {onBack && <button onClick={onBack} style={{ cursor: 'pointer', background: S.accent2 || '#E7B73B', color: '#1A1300', border: 'none', borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 900 }} title="Close this list">✕ Close</button>}
        <span style={{ width: 9, height: 9, borderRadius: 99, background: res.onMasterN ? RED : (S.good || '#4ADE80'), boxShadow: `0 0 8px ${res.onMasterN ? RED : (S.good || '#4ADE80')}` }} />
        <div style={{ fontSize: 17, fontWeight: 900, color: text }}>Camera / GPS not reporting</div>
        <span style={{ fontSize: 12.5, color: text }}>📡 <b style={{ color: res.gpsN ? RED : steel }}>{res.gpsN}</b> GPS · 📷 <b style={{ color: res.camN ? CAM_C : steel }}>{res.camN}</b> camera · <span style={{ color: steel }}>{res.onMasterN} units</span></span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10.5, color: steel }}>{res.generatedAt ? `Geotab pulled ${fmtWhen(res.generatedAt)}` : 'Geotab not pulled yet'}</span>
        <button onClick={printList} style={{ cursor: 'pointer', background: 'transparent', border: `1px solid ${accent}`, borderRadius: 20, padding: '6px 14px', color: S.accent2 || '#FFB020', fontSize: 11.5, fontWeight: 900, textTransform: 'uppercase' }}>🖨 Print</button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search unit, description, assigned, issue…" style={{ ...inp, minWidth: 220, flex: '1 1 220px', maxWidth: 340 }} />
        {chip(kind === 'all', 'All', res.onMasterN, 'all')}
        {chip(kind === 'gps', 'GPS', res.gpsN, 'gps')}
        {chip(kind === 'cam', 'Camera', res.camN, 'cam')}
        <select value={cat} onChange={e => setCat(e.target.value)} style={{ ...inp, cursor: 'pointer' }} title="Type — Equipment Master category">
          <option value="">All types</option>
          {cats.map(([c, n]) => <option key={c} value={c}>{c} ({n})</option>)}
          {res.offMasterN > 0 && <option value="__off">Not on Master ({res.offMasterN})</option>}
        </select>
        <div style={{ flex: 1 }} />
        {res.offMasterN > 0 && cat !== '__off' && (
          <label style={{ fontSize: 11, color: steel, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showOff} onChange={e => setShowOff(e.target.checked)} /> + {res.offMasterN} not on Master
          </label>
        )}
        {res.parkedN > 0 && (
          <label style={{ fontSize: 11, color: steel, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showParked} onChange={e => setShowParked(e.target.checked)} /> Show parked ({res.parkedN})
          </label>
        )}
      </div>

      <div style={{ fontSize: 11, color: steel, marginBottom: 6 }}>{rows.length} shown · click a column heading to sort</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{th('unit', 'Unit')}{th('desc', 'Description')}{th('cat', 'Type')}{th('asg', 'Last assigned')}{th('issue', 'What is wrong')}{th('days', 'Days silent')}{th('last', 'Last heard / seen')}</tr></thead>
          <tbody>
            {rows.map(r => {
              const issues = showParked ? r.issues : r.live;
              return (
                <tr key={r.id}>
                  <td style={{ ...td, fontWeight: 900, whiteSpace: 'nowrap', color: r.onMaster ? accent : steel }}>
                    {r.unit}
                    {shopSet.has(String(r.unit).toUpperCase()) && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 900, color: '#1A1300', background: S.accent2 || '#FFB020', borderRadius: 3, padding: '1px 4px' }}>IN SHOP</span>}
                    {!r.onMaster && <div style={{ fontSize: 9.5, fontWeight: 700 }}>not on Master</div>}
                  </td>
                  <td style={{ ...td, color: steel }}>{r.desc || (r.onMaster ? '' : r.geoName)}</td>
                  <td style={{ ...td, color: steel, whiteSpace: 'nowrap' }}>{r.cat}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{(() => {
                    const a = asg(r);
                    if (!r.onMaster) return null;
                    if (!assign) return <span style={{ color: steel }}>…</span>;
                    if (!a) return <span style={{ color: steel, fontSize: 11 }}>not on Daily ({ASSIGN_WEEKS} wks)</span>;
                    return (<>
                      <span style={{ fontWeight: 800 }}>{a.name}</span>
                      {a.duc && !/^A-/.test(a.duc) ? <span style={{ color: steel, fontSize: 11 }}> · {a.duc}</span> : null}
                      <span style={{ color: steel, fontSize: 11 }}> · {mdy(a.day)}</span>
                      {a.crew && a.crew !== a.name ? <div style={{ fontSize: 10.5, color: steel }}>last crew <b style={{ color: text }}>{a.crew}</b> {mdy(a.crewDay)}</div> : null}
                    </>);
                  })()}</td>
                  <td style={td}>
                    {issues.map(x => (
                      <div key={x.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap', opacity: x.acked ? .55 : 1 }} title={x.detail || ''}>
                        <span style={{ fontSize: 9, fontWeight: 900, borderRadius: 3, padding: '1px 5px', color: '#15171A', background: x.kind === 'cam' ? CAM_C : RED }}>{x.kind === 'cam' ? 'CAM' : 'GPS'}</span>
                        <span>{x.label}</span>
                        {x.acked && <span style={{ fontSize: 10.5, color: steel, fontStyle: 'italic' }}>parked{x.acked.why ? ` — ${x.acked.why}` : ''}{x.acked.by ? ` · ${String(x.acked.by).split('@')[0]}` : ''}</span>}
                        {canAck && !x.acked && (
                          <button onClick={() => { setAckFor({ row: r, iss: x }); setAckWhy(''); }} title="Park this one — it comes back if the unit goes silent again later or throws a new message"
                            style={{ cursor: 'pointer', background: 'transparent', border: `1px solid ${line}`, borderRadius: 10, color: steel, fontSize: 9.5, fontWeight: 800, padding: '0 7px' }}>Got it</button>
                        )}
                        {canAck && x.acked && (
                          <button disabled={busy} onClick={() => unpark(x)} style={{ cursor: 'pointer', background: 'transparent', border: `1px solid ${line}`, borderRadius: 10, color: steel, fontSize: 9.5, fontWeight: 800, padding: '0 7px' }}>Unpark</button>
                        )}
                      </div>
                    ))}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 800, color: r.worst >= 30 ? RED : text }}>{r.worst >= 9999 ? 'never' : r.worst >= 1 ? Math.floor(r.worst) : '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: steel }}>{issues.filter(x => x.when).map(x => <div key={x.key}>{fmtWhen(x.when)}</div>)}</td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: steel, padding: 20 }}>Nothing in this view.</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10.5, color: steel, marginTop: 8 }}>
        GPS = no position in {DARK_DAYS}+ days, Geotab says not communicating, a tracker hardware fault, or Master GPS = NW.
        Camera = a camera message from the tracker in the last {CAM_DAYS} days, or Master Camera = NW.
        {res.retiredSkipped ? ` ${res.retiredSkipped} silent tracker${res.retiredSkipped === 1 ? '' : 's'} on retired units left off.` : ''}
        {res.stockSkipped ? ` ${res.stockSkipped} tracker${res.stockSkipped === 1 ? '' : 's'} never installed (not on the Master, never reported) left off as stock.` : ''}
      </div>

      {ackFor && (
        <div onClick={() => setAckFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: S.panel || '#22252A', border: `1px solid ${line}`, borderRadius: 12, padding: 16, width: '100%', maxWidth: 420 }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: text }}>{ackFor.row.unit} — {ackFor.iss.label}</div>
            <div style={{ fontSize: 11, color: steel, margin: '4px 0 10px' }}>Parks this one. It comes back on its own if the unit reports again and later goes silent, or throws a new message.</div>
            <input value={ackWhy} onChange={e => setAckWhy(e.target.value)} autoFocus placeholder="Why (optional) — parked out of coverage, sent to Geotab, unit for sale…"
              style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => setAckFor(null)} style={{ cursor: 'pointer', background: 'transparent', border: `1px solid ${line}`, borderRadius: 16, color: steel, padding: '5px 12px', fontSize: 11.5, fontWeight: 800 }}>Cancel</button>
              <button disabled={busy} onClick={saveAck} style={{ cursor: 'pointer', background: accent, border: 'none', borderRadius: 16, color: '#1A1300', padding: '5px 14px', fontSize: 11.5, fontWeight: 900 }}>{busy ? 'Saving…' : 'Got it'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
